import {
  MedicalDirectorSidebar,
  PropertyBagValue,
} from '@/integrations/medical-director/medicalDirectorSidebar';
import {
  trackIntegrationCreated,
  trackIntegrationSessionCreated,
} from '@/lib/analytics/scribe/integrations';
import { trackMedicalDirectorGetDemographicsFailed } from '@/lib/analytics/scribe/medical-director';
import { isValidEHRPatientId } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import {
  setHasIntegration,
  setIntegrationCallbacks,
  setIntegrationData,
  setIntegrationOptions,
} from '@heidi/integrations/ehr/data/atoms';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { useLatestRef } from '@heidi/utils';
import { useEffect, useState } from 'react';
import {
  type MedicalDirectorPractitionerDetails,
  type MedicalDirectorSidebarDemographics,
  type MedicalDirectorSidebarEvent,
} from '../types';

type IntegrationCallbacks = {
  onPushNotes: (notes: string) => void;
  onSwitchPatient: (newPatientId: string) => void;
  onCheckCurrentPatient: () => string | undefined;
};

type UseMdSidebarParams = {
  medicalDirector: MedicalDirectorSidebar | null;
  setMedicalDirector: (instance: MedicalDirectorSidebar) => void;
  processDemographicsResponse: (data: MedicalDirectorSidebarDemographics) => void;
  processPractitionerResponse: (data: MedicalDirectorPractitionerDetails) => void;
  integrationCallbacks: IntegrationCallbacks;
  mdEventHandler: (event: MedicalDirectorSidebarEvent) => void;
};

const getEhrBaseUrl = () =>
  getEnvironment() === ENV_NAME.PROD
    ? 'https://smartscribe.telstrahealth.com/'
    : 'https://smartscribe-widget-dev.azurewebsites.net/?testfor=AFbolTrPfe4U9VFAOl7TGg%3d%3d';

// Drives the Medical Director sidebar's full lifecycle: instance creation,
// post-creation layout/options/callback wiring, initial demographics fetch,
// event subscription, and analytics. The parent owns `medicalDirector` state
// so that the action callbacks it defines can close over the live instance.
export const useMdSidebar = ({
  medicalDirector,
  setMedicalDirector,
  processDemographicsResponse,
  processPractitionerResponse,
  integrationCallbacks,
  mdEventHandler,
}: UseMdSidebarParams) => {
  const [creatingMedicalDirectorSidebar, setCreatingMedicalDirectorSidebar] =
    useState(false);

  // Callbacks read via latestRef so the parent can pass freshly defined
  // closures every render without re-running the setup effect.
  const processDemographicsResponseRef = useLatestRef(processDemographicsResponse);
  const processPractitionerResponseRef = useLatestRef(processPractitionerResponse);
  const integrationCallbacksRef = useLatestRef(integrationCallbacks);
  const mdEventHandlerRef = useLatestRef(mdEventHandler);

  // Sidebar instance creation
  useEffect(() => {
    logger.debug('[MD]', medicalDirector, creatingMedicalDirectorSidebar);

    if (creatingMedicalDirectorSidebar || medicalDirector) return;

    logger.debug('[MD]', 'Creating new Medical Director sidebar');
    setCreatingMedicalDirectorSidebar(true);

    let timerId: NodeJS.Timeout | undefined;
    if (MedicalDirectorSidebar.isMedicalDirector()) {
      timerId = setTimeout(() => {
        document.body.style.height = `${window.screen.availHeight}px`;
        document.body.style.setProperty('position', 'relative', 'important');
      }, 100);

      logger.debug('[MD]', 'Start create new Medical Director sidebar');
      trackIntegrationSessionCreated({
        integration_type: 'medical_director',
      });

      setMedicalDirector(
        new MedicalDirectorSidebar({
          processDemographicsResponse: (data) =>
            processDemographicsResponseRef.current(data),
          processPractitionerResponse: (data) =>
            processPractitionerResponseRef.current(data),
        })
      );

      logger.debug('[MD]', 'Created new Medical Director sidebar');
    }
    setCreatingMedicalDirectorSidebar(false);

    return () => {
      if (timerId) clearTimeout(timerId);
    };
    // Only execute this when init medical director sidebar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalDirector]);

  // Post-creation setup: layout, options, initial demographics, callbacks,
  // event subscription, analytics.
  useEffect(() => {
    if (!medicalDirector) return;

    medicalDirector.layoutWidget();

    setHasIntegration(true);
    setIntegrationData({
      ehrProvider: EHRProvider.Medical_Director,
    });

    setIntegrationOptions({
      ehrName: 'Medical Director',
      banner: true,
      ehrBaseUrl: getEhrBaseUrl(),
      pushNotes: true,
      patientDemographics: true,
    });

    medicalDirector.send('GetPractitionerRequest', {
      ResourceId: medicalDirector.readPropertyBagValue(PropertyBagValue.CURRENT_USER_ID),
    });

    const patientId = medicalDirector.readPropertyBagValue(
      PropertyBagValue.CURRENT_PATIENT_ID
    );

    if (isValidEHRPatientId(patientId)) {
      try {
        medicalDirector.send('GetDemographicsRequest', {
          PatientID: Number(patientId),
        });
        setIntegrationData({
          patientId: String(patientId),
        });
      } catch (error) {
        logger.error('[MD]', 'Failed to fetch demographics:', error);
        trackMedicalDirectorGetDemographicsFailed(
          error instanceof Error ? error.message : 'Unknown error'
        );
      }
    }

    // Wrap the callbacks so the atom holds stable references that delegate
    // to the latest closures defined in the parent.
    setIntegrationCallbacks({
      onPushNotes: (notes) => integrationCallbacksRef.current.onPushNotes(notes),
      onSwitchPatient: (id) => integrationCallbacksRef.current.onSwitchPatient(id),
      onCheckCurrentPatient: () =>
        integrationCallbacksRef.current.onCheckCurrentPatient(),
    });

    const eventHandler = (event: MedicalDirectorSidebarEvent) =>
      mdEventHandlerRef.current(event);
    medicalDirector.Events.subscribe(medicalDirector.Esp.onMDEvent, eventHandler);
    trackIntegrationCreated({
      integration_type: 'medical_director',
    });

    return () => {
      medicalDirector.Events.unsubscribe(medicalDirector.Esp.onMDEvent, eventHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalDirector]);
};
